import React from "react";
import { Check, X, Star } from "lucide-react";

// Pricing tier comparison matrix for the homepage.
//
// Tier names + prices are taken verbatim from the existing pre-release plan
// definitions in PublicPages.jsx (Starter / Professional / Advanced at
// $79 / $129 / $179 per month). Keep these in sync if the canonical plans
// there change. "Professional" is the recommended tier (teal accent).
const TIERS = [
  { id: "starter", name: "Starter", price: "$79", recommended: false },
  { id: "professional", name: "Professional", price: "$129", recommended: true },
  { id: "advanced", name: "Advanced", price: "$179", recommended: false },
];

// Each feature row carries one cell per tier (Starter, Professional, Advanced).
// A cell is `true` (✅), `false` (❌), or a string (rendered verbatim, e.g. "5").
const FEATURES = [
  { label: "Protocol Library access", cells: [true, true, true] },
  { label: "Dr. Peptide AI chat", cells: ["50 msgs/mo", "Unlimited", "Unlimited"] },
  { label: "Personalized protocol generation", cells: [false, true, true] },
  { label: "Patient records", cells: ["5", "50", "Unlimited"] },
  { label: "PubMed-cited responses", cells: [true, true, true] },
  { label: "PDF export & print", cells: [true, true, true] },
  { label: "Lab analysis", cells: [false, true, true] },
  { label: "Genetics analysis", cells: [false, false, true] },
  { label: "Multi-practitioner accounts", cells: [false, false, true] },
  { label: "Priority support", cells: [false, false, true] },
];

const Cell = ({ value }) => {
  if (value === true) {
    return <Check className="h-4 w-4 text-teal-600 mx-auto" aria-label="Included" />;
  }
  if (value === false) {
    return <X className="h-4 w-4 text-slate-300 mx-auto" aria-label="Not included" />;
  }
  return <span className="text-xs md:text-sm text-slate-700">{value}</span>;
};

const PricingComparisonMatrix = () => {
  return (
    <section className="container mx-auto px-4 py-14 md:py-20" data-testid="pricing-comparison-matrix">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-800 mb-2">Compare Plans</h2>
        <p className="text-center text-slate-400 mb-10 text-sm">Choose the tier that fits your practice.</p>

        {/* Desktop / tablet: comparison table with sticky header (sm and up) */}
        <div className="hidden sm:block overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white">
                <th className="text-left text-sm font-semibold text-slate-500 p-4 border-b border-slate-200">Features</th>
                {TIERS.map((tier) => (
                  <th
                    key={tier.id}
                    className={`p-4 border-b text-center ${tier.recommended ? "border-teal-500 bg-teal-50" : "border-slate-200"}`}
                    data-testid={`tier-header-${tier.id}`}
                  >
                    {tier.recommended && (
                      <div className="flex items-center justify-center gap-1 text-[11px] font-semibold text-teal-700 mb-1">
                        <Star className="h-3 w-3 fill-teal-500 text-teal-500" /> Recommended
                      </div>
                    )}
                    <div className={`font-bold ${tier.recommended ? "text-teal-700" : "text-slate-800"}`}>{tier.name}</div>
                    <div className="text-sm text-slate-500">
                      <span className={`font-bold ${tier.recommended ? "text-teal-700" : "text-slate-700"}`}>{tier.price}</span>/mo
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((feature, i) => (
                <tr key={feature.label} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                  <td className="text-sm text-slate-700 p-4 border-b border-slate-100">{feature.label}</td>
                  {feature.cells.map((value, idx) => (
                    <td
                      key={idx}
                      className={`p-4 border-b border-slate-100 text-center ${TIERS[idx].recommended ? "bg-teal-50/50" : ""}`}
                    >
                      <Cell value={value} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards, one per tier */}
        <div className="sm:hidden space-y-4">
          {TIERS.map((tier, tierIdx) => (
            <div
              key={tier.id}
              className={`rounded-xl border p-5 ${tier.recommended ? "border-teal-500 bg-teal-50/60" : "border-slate-200 bg-white"}`}
              data-testid={`tier-card-${tier.id}`}
            >
              {tier.recommended && (
                <div className="flex items-center gap-1 text-[11px] font-semibold text-teal-700 mb-1">
                  <Star className="h-3 w-3 fill-teal-500 text-teal-500" /> Recommended
                </div>
              )}
              <div className="flex items-baseline justify-between mb-4">
                <span className={`text-lg font-bold ${tier.recommended ? "text-teal-700" : "text-slate-800"}`}>{tier.name}</span>
                <span className="text-sm text-slate-500">
                  <span className={`font-bold ${tier.recommended ? "text-teal-700" : "text-slate-700"}`}>{tier.price}</span>/mo
                </span>
              </div>
              <ul className="space-y-2">
                {FEATURES.map((feature) => (
                  <li key={feature.label} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-600">{feature.label}</span>
                    <span className="flex-shrink-0"><Cell value={feature.cells[tierIdx]} /></span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingComparisonMatrix;
